import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button as MuiButton,
  Chip,
  FormControl,
  Grid,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Mic, StopCircle, Upload, Video } from 'lucide-react';
import { toast } from '../../../../../../ui/toast';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../../i18n/constants/keys';
import { Button } from '../../../../../../ui';
import { JobPostingTabData } from '../../../../../../utils/types/JobOpenings';
import { SelectOptions } from '../../../../../../utils/types/react_select_types';

interface VideoData {
  title: string;
  description: string;
  videoUrl: string | null;
}

export interface VideoMessageDetailsProps {
  setActiveTab: (value: ((prevState: string) => string) | string) => void;
  jobPostingData: JobPostingTabData;
  setJobPostingData: React.Dispatch<React.SetStateAction<JobPostingTabData>>;
}

export const VideoMessageDetails: React.FC<VideoMessageDetailsProps> = ({
  setActiveTab,
  jobPostingData,
  setJobPostingData,
}) => {
  const { t } = useTranslation(NS.adminPages);
  const [, setVideoData] = useState<VideoData>({
    title: '',
    description: '',
    videoUrl: null,
  });
  const [isRecording, setIsRecording] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunks: Blob[] = [];
  const [micList, setMicList] = useState<SelectOptions[]>([]);
  const [selectedMicId, setSelectedMicId] = useState<string | null>(null);

  useEffect(() => {
    const getMicrophones = async () => {
      if (jobPostingData.enableVideo) {
        try {
          await navigator.mediaDevices.getUserMedia({
            audio: true,
            video: true,
          });

          const devices = await navigator.mediaDevices.enumerateDevices();
          const mics = devices
            .filter((device) => device.kind === 'audioinput')
            .map((device) => ({
              value: device.deviceId,
              label: device.label || 'Microphone',
            }));
          setMicList(mics);
          if (mics.length > 0) {
            setSelectedMicId(mics[0].value);
          }
        } catch {
          toast.error('Error accessing microphone devices:', {
            position: 'bottom-center',
          });
        }
      } else {
        setMicList([]);
        setSelectedMicId(null);
      }
    };

    getMicrophones();
  }, [jobPostingData.enableVideo]);

  const startRecording = async () => {
    if (!jobPostingData.enableVideo) {
      toast.error('Please enable video posting first', {
        position: 'bottom-center',
      });
      return;
    }

    if (selectedMicId) {
      try {
        const constraints = {
          video: {
            width: { ideal: 640 },
            height: { ideal: 480 },
          },
          audio: {
            deviceId: selectedMicId,
          },
        };

        const stream = await navigator.mediaDevices.getUserMedia(constraints);

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.muted = true;
          await videoRef.current.play();
        }

        mediaRecorderRef.current = new MediaRecorder(stream);
        mediaRecorderRef.current.ondataavailable = (event) => {
          recordedChunks.push(event.data);
        };
        mediaRecorderRef.current.onstop = () => {
          const blob = new Blob(recordedChunks, { type: 'video/webm' });
          const url = URL.createObjectURL(blob);

          setVideoData((prev) => ({ ...prev, videoUrl: url }));
          setJobPostingData({
            ...jobPostingData,
            videoFilename: new File([blob], 'recorded-video.mp4', {
              type: 'video/webm',
            }),
          });

          stream.getTracks().forEach((track) => track.stop());

          if (videoRef.current) {
            videoRef.current.srcObject = null;
            videoRef.current.src = url;
            videoRef.current.play();
          }
        };

        mediaRecorderRef.current.start();
        setIsRecording(true);
      } catch {
        toast.error('Error accessing media devices.', {
          position: 'bottom-center',
        });
      }
    } else {
      toast.error('Please select a Mic option before you record', {
        position: 'bottom-center',
      });
    }
  };

  const stopRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== 'inactive'
    ) {
      const stream = videoRef.current?.srcObject as MediaStream;
      stream?.getTracks().forEach((track) => track.stop());

      mediaRecorderRef.current.stop();

      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }

      setIsRecording(false);
    }
  };

  const handleUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!jobPostingData.enableVideo) {
      toast.error('Please enable video posting first', {
        position: 'bottom-center',
      });
      return;
    }

    const file = event.target.files?.[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setJobPostingData({ ...jobPostingData, videoFilename: file });
      setVideoData((prev) => ({ ...prev, videoUrl: url }));

      if (videoRef.current) {
        videoRef.current.srcObject = null;
        videoRef.current.src = url;
        videoRef.current.play();
      }
    }
  };

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = event.target;
    setVideoData((prev) => ({ ...prev, [name]: value }));
    if (name === 'description') {
      setJobPostingData({ ...jobPostingData, videoDescription: value });
    } else if (name === 'title') {
      setJobPostingData({ ...jobPostingData, videoTitle: value });
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        p: { xs: 2, sm: 3 },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          pb: 2,
          mb: 3,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant='h6' sx={{ fontWeight: 700, color: 'text.primary' }}>
          {t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.video.heading)}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant='body2' sx={{ fontWeight: 600, color: 'text.primary' }}>
            {t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.video.useVideo)}
          </Typography>
          <Switch
            checked={!!jobPostingData.enableVideo}
            onChange={(e) => {
              setJobPostingData({
                ...jobPostingData,
                enableVideo: e.target.checked,
                ...(e.target.checked
                  ? {}
                  : {
                      videoFilename: undefined,
                      videoTitle: '',
                      videoDescription: '',
                    }),
              });
              if (!e.target.checked) {
                setVideoData({
                  title: '',
                  description: '',
                  videoUrl: null,
                });
                if (videoRef.current) {
                  videoRef.current.src = '';
                }
              }
            }}
            color='primary'
          />
          {jobPostingData.enableVideo && jobPostingData.videoFilename && (
            <Chip
              label={t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.video.videoReady)}
              size='small'
              color='success'
              variant='outlined'
              sx={{ fontWeight: 600 }}
            />
          )}
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Box>
              <Typography variant='caption' sx={{ display: 'block', mb: 0.75, fontWeight: 600, color: 'text.secondary' }}>
                {t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.video.selectMicrophone)}
              </Typography>
              <FormControl fullWidth size='small' disabled={!jobPostingData.enableVideo}>
                <Select
                  value={selectedMicId || ''}
                  onChange={(e) => setSelectedMicId(e.target.value)}
                  displayEmpty
                  startAdornment={<Mic size={16} style={{ marginRight: 8, opacity: 0.7 }} />}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value=''>
                    <em>{t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.video.chooseAudioDevice)}</em>
                  </MenuItem>
                  {micList.map((mic) => (
                    <MenuItem key={mic.value} value={mic.value}>
                      {mic.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box>
              <Typography variant='caption' sx={{ display: 'block', mb: 0.75, fontWeight: 600, color: 'text.secondary' }}>
                {t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.video.titleLabel)}
              </Typography>
              <TextField
                fullWidth
                size='small'
                name='title'
                value={jobPostingData.videoTitle || ''}
                onChange={handleInputChange}
                placeholder={t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.video.titlePlaceholder)}
                disabled={!jobPostingData.enableVideo}
                InputProps={{ sx: { borderRadius: 2 } }}
              />
            </Box>

            <Box>
              <Typography variant='caption' sx={{ display: 'block', mb: 0.75, fontWeight: 600, color: 'text.secondary' }}>
                {t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.video.description)}
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                size='small'
                name='description'
                value={jobPostingData.videoDescription || ''}
                onChange={handleInputChange}
                placeholder={t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.video.descriptionPlaceholder)}
                disabled={!jobPostingData.enableVideo}
                InputProps={{ sx: { borderRadius: 2 } }}
              />
            </Box>

            <Typography variant='caption' sx={{ color: 'warning.main', fontWeight: 500 }}>
              {t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.video.note)}
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper
            variant='outlined'
            sx={{
              p: 2,
              borderRadius: 2.5,
              bgcolor: 'action.hover',
              opacity: !jobPostingData.enableVideo ? 0.5 : 1,
              pointerEvents: !jobPostingData.enableVideo ? 'none' : 'auto',
            }}
          >
            <Box
              sx={{
                borderRadius: 2,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: '#000',
                mb: 2,
              }}
            >
              <Box
                component='video'
                ref={videoRef}
                controls={!isRecording && !!jobPostingData.enableVideo}
                sx={{ width: '100%', height: 260, display: 'block' }}
              >
                {t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.video.browserNotSupported)}
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <MuiButton
                onClick={isRecording ? stopRecording : startRecording}
                disabled={!jobPostingData.enableVideo}
                variant='contained'
                color={isRecording ? 'error' : 'primary'}
                startIcon={isRecording ? <StopCircle size={16} /> : <Video size={16} />}
                fullWidth
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
              >
                {isRecording
                  ? t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.video.stopRecording)
                  : t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.video.startRecording)}
              </MuiButton>

              <MuiButton
                component='label'
                disabled={!jobPostingData.enableVideo}
                variant='outlined'
                startIcon={<Upload size={16} />}
                fullWidth
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
              >
                {t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.video.uploadVideo)}
                <input
                  id='file-upload'
                  type='file'
                  accept='video/*'
                  onChange={handleUpload}
                  style={{ display: 'none' }}
                  disabled={!jobPostingData.enableVideo}
                />
              </MuiButton>
            </Box>

            <Typography variant='caption' sx={{ display: 'block', mt: 1.5, textAlign: 'center', color: 'text.secondary' }}>
              {t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.video.supportedFormats)}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button
          onClick={() => setActiveTab('preQualifyingQuestions')}
          variant='primary'
          label={t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.video.continue)}
        />
      </Box>
    </Paper>
  );
};

export default VideoMessageDetails;
